import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/api";

/**
 * ============================================================
 *  GET NOTIFICATIONS  (customer or seller)
 * ============================================================
 */
export const get_notifications = createAsyncThunk(
    "notification/get_notifications",
    async (userId, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/notifications/${userId}`);
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

/**
 * ============================================================
 *  MARK AS READ (single)
 * ============================================================
 */
export const mark_as_read = createAsyncThunk(
    "notification/mark_as_read",
    async (notificationId, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.put(`/notifications/read/${notificationId}`);
            return fulfillWithValue({ notificationId, ...data });
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

/**
 * ============================================================
 *  MARK ALL AS READ
 * ============================================================
 */
export const mark_all_read = createAsyncThunk(
    "notification/mark_all_read",
    async (userId, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.put(`/notifications/read-all/${userId}`);
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

/**
 * ============================================================
 *  DELETE NOTIFICATION
 * ============================================================
 */
export const delete_notification = createAsyncThunk(
    "notification/delete_notification",
    async (notificationId, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.delete(`/notifications/${notificationId}`);
            return fulfillWithValue({ notificationId, ...data });
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);


/**
 * ============================================================
 *  REDUCER
 * ============================================================
 */
export const notificationReducer = createSlice({
    name: "notification",
    initialState: {
        notifications: [],
        unreadCount: 0,
        loading: false,
        errorMessage: "",
        successMessage: "",
    },
    reducers: {
        messageClear: (state) => {
            state.errorMessage = "";
            state.successMessage = "";
        },

        /**
         * For receiving real-time notification from SOCKET.IO
         */
        push_notification: (state, { payload }) => {
            state.notifications.unshift(payload);
            state.unreadCount = state.unreadCount + 1;
        },

        markAllReadLocal: (state) => {
  state.notifications = state.notifications.map(n => ({
    ...n,
    isRead: true
  }));
}

    },

    extraReducers: (builder) => {
        builder

            // -------------------------------------------------
            // GET NOTIFICATIONS
            // -------------------------------------------------
            .addCase(get_notifications.pending, (state) => {
                state.loading = true;
            })
            .addCase(get_notifications.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.notifications = payload.notifications;
                state.unreadCount = payload.unreadCount;
            })
            .addCase(get_notifications.rejected, (state, { payload }) => {
                state.loading = false;
                state.errorMessage = payload.error;
            })

            // -------------------------------------------------
            // MARK AS READ
            // -------------------------------------------------
            .addCase(mark_as_read.fulfilled, (state, { payload }) => {
                const id = payload.notificationId;
                state.notifications = state.notifications.map((n) =>
                    n._id === id ? { ...n, isRead: true } : n
                );
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            })

            // -------------------------------------------------
            // MARK ALL READ
            // -------------------------------------------------
            .addCase(mark_all_read.fulfilled, (state) => {
                state.notifications = state.notifications.map((n) => ({
                    ...n,
                    isRead: true,
                }));
                state.unreadCount = 0;
            })

            // -------------------------------------------------
            // DELETE NOTIFICATION
            // -------------------------------------------------
            .addCase(delete_notification.fulfilled, (state, { payload }) => {
                const id = payload.notificationId;
                const deleted = state.notifications.find((n) => n._id === id);

                if (deleted && !deleted.isRead) {
                    state.unreadCount = Math.max(0, state.unreadCount - 1);
                }

                state.notifications = state.notifications.filter(
                    (n) => n._id !== id
                );
            });
    },
});

export const { messageClear, push_notification, markAllReadLocal } = notificationReducer.actions;
export default notificationReducer.reducer;
